import {
  faBell,
  faBook,
  faEarthAfrica,
  faEarthAmericas,
  faEarthAsia,
  faEarthEurope,
  faEarthOceania,
  faEnvelope,
  faFilm,
  faFire,
  faGavel,
  faGear,
  faMoon,
  faRightFromBracket,
  faRightToBracket,
  faSun,
  faTrophy,
  faUser,
  faUserFriends,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImgPremium from 'jsx:~/static/premium.svg';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';

import { ff } from '../../../common/flag';
import { LS_DARK_MODE } from '../../../config';
import Action from '../../Action';
import Button from '../../Atoms/Button';
import Divider from '../../Atoms/Divider';
import Logo from '../../Atoms/Logo';
import UserPicture from '../../Atoms/UserPicture';

import * as style from './style.module.css';

const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'Europe/Paris';

// Choose the right Erth icon based on timezone
const faLanguageIcon =
  {
    Africa: faEarthAfrica,
    America: faEarthAmericas,
    Asia: faEarthAsia,
    Australia: faEarthOceania,
    Indian: faEarthAsia,
    Pacific: faEarthOceania,
  }[timezone.split('/')[0]] || faEarthEurope;

const parseHint = (nb) => {
  if (!nb || nb <= 0) {
    return '';
  }
  if (nb > 99) {
    return '99+';
  }
  return `${nb}`;
};

const UserMenuAction = ({ action, title, icon, type = null }) => <Action action={action} className={`${style.userMenuAction} ${type === 'PREMIUM' ? style.userMenuActionPremium : ''} ${type === 'LOGOUT' ? style.logout : ''}`}>
  {icon && type !== 'PREMIUM' && <FontAwesomeIcon icon={icon} />}
  {type === 'PREMIUM' && <ImgPremium />}
  <span>{title}</span>
</Action>;

const UserMenu = withTranslation()(({ t, contestsToJudge = [], darkModeStatus, showAdmin, isOpened, onThemeChange = () => { }, onClose = () => { }, onSignOut = () => { }, user = null }) => {
  const showDarkMode = ff('darkmode');
  const navigate = useNavigate();

  const closeAndGoTo = (path) => () => {
    onClose();
    navigate(path);
  };

  return <>
    <div className={`${style.closeLayer} ${isOpened ? style.isOpened : ''}`} onClick={onClose} />
    <div className={`${style.userMenu} ${isOpened ? style.isOpened : ''}`}>
      <UserMenuAction action={closeAndGoTo(`/users/${user.id}/${user.slug}`)} icon={faUser} title={t('My profile')} />
      <UserMenuAction action={closeAndGoTo(`/users/${user.id}/${user.slug}/edit`)} icon={faGear} title={t('Settings')} />
      {!user.isPremium && <UserMenuAction type="PREMIUM" action={closeAndGoTo(`/offers/`)} title={t('Become premium')} />}
      {contestsToJudge.map(contest => <UserMenuAction key={contest?.id} action={closeAndGoTo(`/contests/${contest.id}/judge/${user.id}`)} icon={faGavel} title={contestsToJudge.length > 1 ? t('Judge ({{title}})', { title: contest.shortTitle }) : t('Judge contest')} />)}
      {showDarkMode && darkModeStatus === 'LIGHT' && <UserMenuAction action={() => onThemeChange('DARK')} icon={faMoon} title={t('Dark theme')} />}
      {showDarkMode && darkModeStatus === 'DARK' && <UserMenuAction action={() => onThemeChange('LIGHT')} icon={faSun} title={t('Light theme')} />}
      {showAdmin && <UserMenuAction icon={faWrench} action={closeAndGoTo(`/admin/`)} title={t('Admin')} />}
      <Divider />
      <UserMenuAction  type="LOGOUT" action={onSignOut} icon={faRightFromBracket} title={t('Sign out')} />
    </div>
  </>
});

const Header = ({ t, contest = null, contestsToJudge = [], user = null, allowedActions = [], unreadChats = 0, unreadNotifications = 0, loading = false, onSignOut, onLanguageClick = () => { } }) => {
  const isKeyboardOpen = useDetectKeyboardOpen();
  const isConnected = !!user;
  const showAdmin = allowedActions.some(e => e.startsWith('ADMIN_')) || allowedActions.includes('READ_DRAFT_ARTICLES');
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [, setTheme] = useState(localStorage.getItem(LS_DARK_MODE) === 'DARK' ? 'DARK' : 'LIGHT');
  const darkModeStatus = localStorage.getItem(LS_DARK_MODE) === 'DARK' ? 'DARK' : 'LIGHT';

  const handleThemeChange = (newTheme) => {
    localStorage.setItem(LS_DARK_MODE, newTheme);
    document.body.className = localStorage.getItem(LS_DARK_MODE) === 'DARK' ? 'dark' : 'light';
    setTheme(newTheme);
  }

  return (
    <header className={style.header}>
      <div className={style.headerLeft}>
        <Action action="/" title={t('Home')}>
          <Logo className={style.logo} />
        </Action>
      </div>
      <div className={`${style.headerCenter} ${isKeyboardOpen ? style.keyboardOpen : ''}`}>
        <Action action="/news/" className={style.category}>
          <FontAwesomeIcon icon={faFire} />
          <span>{t('News')}</span>
        </Action>
        {contest && <Action action={`/contests/${contest.id}/${contest.slug}`} className={style.category}>
          <FontAwesomeIcon icon={faTrophy} />
          <span>{contest.shortTitle}</span>
        </Action>}
        <Action action="/brickfilms/" className={style.category}>
          <FontAwesomeIcon icon={faFilm} />
          <span>{t('Brickfilms')}</span>
        </Action>
        <Action action="/community/" className={style.category}>
          <FontAwesomeIcon icon={faUserFriends} />
          <span>{t('Community')}</span>
        </Action>
        <Action action="/resources/" className={style.category}>
          <FontAwesomeIcon icon={faBook} />
          <span>{t('Resources')}</span>
        </Action>
      </div>
      <div className={style.headerRight}>
        {!loading && isConnected && (
          <>
            <UserPicture {...user} title={t('User profile')} className={style.user} animatedPicture={null} action={() => setIsMenuOpened(true)} />
            <Button className={style.actionNotif} action={`/chats/`} title={t('Chats')} hint={parseHint(unreadChats)} icon={faEnvelope} />
            <Button className={style.actionNotif} action={`/notifications/?t=${new Date().getTime()}`} hint={parseHint(unreadNotifications)} icon={faBell} />
          </>
        )}
        {!loading && !isConnected && (
          <>
            <Button className={style.signInButton} action="/sign-in/" label={t('Sign in')} icon={faRightToBracket} />
          </>
        )}
        <Button className={showAdmin ? style.forceHideOnMobile : ''} icon={faLanguageIcon} title={t('Language')} action={!loading ? onLanguageClick : null} />

        {user && <UserMenu contestsToJudge={contestsToJudge} onThemeChange={handleThemeChange} darkModeStatus={darkModeStatus} showAdmin={showAdmin} isOpened={isMenuOpened} onClose={() => setIsMenuOpened(false)} onSignOut={onSignOut} user={user} />}
      </div>
      {loading && <div className={style.headerRight} />}
    </header>
  );
};

export default withTranslation()(Header);

Header.propTypes = {
  t: PropTypes.func,
};
